import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { analyticsAPI, childrenAPI, betaAPI, adminAPI } from '../api';
import api from '../api';
import { toast } from 'sonner';
import { 
  ArrowLeft, Users, Clock, BookOpen, Flame, Sparkles, 
  TrendingUp, TrendingDown, BarChart3, Activity, Target,
  Star, AlertTriangle, Award, Calendar, Mail, Copy, Send,
  Check, X, UserPlus, RefreshCw, Trash2, Link2, MessageSquare,
  Bug, Lightbulb, HelpCircle, Heart, Eye, Music, Gamepad2,
  Mic, ChevronRight, ChevronDown, Search, Key, User, History, Wrench,
  PenTool, Book, Puzzle, MapPin
} from 'lucide-react';

// Stat Card component - now clickable
const StatCard = ({ icon: Icon, label, value, subValue, color = 'indigo', trend, onClick, clickable }) => (
  <div 
    className={`bg-slate-800/30 border border-slate-700/50 rounded-xl p-4 transition-all ${
      clickable ? 'cursor-pointer hover:bg-slate-700/30 hover:border-slate-600/50 hover:scale-[1.02]' : ''
    }`}
    onClick={onClick}
    data-testid={`stat-card-${label.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <div className="flex items-start justify-between">
      <div className={`w-10 h-10 rounded-lg bg-${color}-500/20 flex items-center justify-center`}>
        <Icon className={`w-5 h-5 text-${color}-400`} />
      </div>
      <div className="flex items-center gap-2">
        {trend !== undefined && (
          <div className={`flex items-center gap-1 text-xs ${trend >= 0 ? 'text-green-400' : 'text-red-400'}`}>
            {trend >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            {Math.abs(trend)}%
          </div>
        )}
        {clickable && <ChevronRight className="w-4 h-4 text-slate-500" />}
      </div>
    </div>
    <div className="mt-3">
      <div className="text-2xl font-bold text-white">{value}</div>
      <div className="text-sm text-slate-400">{label}</div>
      {subValue && <div className="text-xs text-slate-500 mt-1">{subValue}</div>}
    </div>
  </div>
);

// Progress Bar component
const ProgressBar = ({ label, value, max, color = 'indigo' }) => (
  <div className="mb-3">
    <div className="flex items-center justify-between text-sm mb-1">
      <span className="text-slate-300">{label}</span>
      <span className="text-slate-400">{value}/{max}</span>
    </div>
    <div className="h-2 bg-slate-700/50 rounded-full overflow-hidden">
      <div 
        className={`h-full bg-${color}-500 rounded-full transition-all`}
        style={{ width: `${(value / max) * 100}%` }}
      />
    </div>
  </div>
);

// Child Analytics Card
const ChildAnalyticsCard = ({ analytics, childName }) => (
  <div className="bg-slate-800/30 border border-slate-700/50 rounded-2xl p-6">
    <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
      <Star className="w-5 h-5 text-yellow-400" />
      {childName}'s Progress
    </h3>
    
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
      <div className="text-center p-3 bg-slate-900/50 rounded-xl">
        <Clock className="w-5 h-5 text-blue-400 mx-auto mb-1" />
        <div className="text-xl font-bold text-white">{analytics.time_spent_today_minutes}</div>
        <div className="text-xs text-slate-400">min today</div>
      </div>
      <div className="text-center p-3 bg-slate-900/50 rounded-xl">
        <BookOpen className="w-5 h-5 text-green-400 mx-auto mb-1" />
        <div className="text-xl font-bold text-white">{analytics.lessons_completed_today}</div>
        <div className="text-xs text-slate-400">lessons today</div>
      </div>
      <div className="text-center p-3 bg-slate-900/50 rounded-xl">
        <Flame className="w-5 h-5 text-orange-400 mx-auto mb-1" />
        <div className="text-xl font-bold text-white">{analytics.current_streak}</div>
        <div className="text-xs text-slate-400">day streak</div>
      </div>
      <div className="text-center p-3 bg-slate-900/50 rounded-xl">
        <Sparkles className="w-5 h-5 text-yellow-400 mx-auto mb-1" />
        <div className="text-xl font-bold text-white">{analytics.total_sparks}</div>
        <div className="text-xs text-slate-400">sparks</div>
      </div>
    </div>
    
    {/* Skills */}
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div>
        <h4 className="text-sm font-medium text-slate-300 mb-2 flex items-center gap-1">
          <TrendingUp className="w-4 h-4 text-green-400" />
          Strongest Skills
        </h4>
        <div className="flex flex-wrap gap-2">
          {(analytics.strongest_skills || []).map(skill => (
            <span key={skill} className="px-2 py-1 bg-green-500/20 text-green-300 rounded-full text-xs">
              {skill}
            </span>
          ))}
          {(!analytics.strongest_skills || analytics.strongest_skills.length === 0) && (
            <span className="text-slate-500 text-xs">Keep learning to find strengths!</span>
          )}
        </div>
      </div>
      <div>
        <h4 className="text-sm font-medium text-slate-300 mb-2 flex items-center gap-1">
          <Target className="w-4 h-4 text-orange-400" />
          Needs Practice
        </h4>
        <div className="flex flex-wrap gap-2">
          {(analytics.skills_needing_practice || []).map(skill => (
            <span key={skill} className="px-2 py-1 bg-orange-500/20 text-orange-300 rounded-full text-xs">
              {skill}
            </span>
          ))}
          {(!analytics.skills_needing_practice || analytics.skills_needing_practice.length === 0) && (
            <span className="text-slate-500 text-xs">Great progress on all skills!</span>
          )}
        </div>
      </div>
    </div>
  </div>
);

const AnalyticsDashboardPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('parent'); // 'parent' or 'admin'
  const [adminTab, setAdminTab] = useState('overview'); // 'overview', 'users'
  const [userTierFilter, setUserTierFilter] = useState('all'); // 'all', 'free', 'family', 'founder'
  const [excludeTestEmails, setExcludeTestEmails] = useState(true); // Filter out test/demo emails by default
  const [showBetaInvites, setShowBetaInvites] = useState(false); // Collapsed by default
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState(null);
  const [childAnalytics, setChildAnalytics] = useState(null);
  const [platformAnalytics, setPlatformAnalytics] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [betaInvites, setBetaInvites] = useState([]);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [newInvite, setNewInvite] = useState({ parent_name: '', parent_email: '', notes: '' });
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedInviteForEmail, setSelectedInviteForEmail] = useState(null);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [betaFeedback, setBetaFeedback] = useState([]);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  
  // Test email patterns to filter out
  const TEST_EMAIL_PATTERNS = [
    'test@', 'test.', 'demo@', 'demo.', 
    'example@', 'example.', 'fake@', 'fake.',
    '@test.com', '@demo.com', '@example.com',
    '@mailinator.com', '@tempmail.com', '@guerrillamail.com',
    'temple.com', // internal testing emails
    '+test', // plus addressing for tests
  ];
  
  // Check if an email looks like a test email
  const isTestEmail = (email) => {
    if (!email) return false;
    const lowerEmail = email.toLowerCase();
    return TEST_EMAIL_PATTERNS.some(pattern => lowerEmail.includes(pattern));
  };
  
  // Drill-down state
  const [drillDownModal, setDrillDownModal] = useState(null); // 'activity' | 'learning' | 'creative' | 'reading' | 'child'
  const [drillDownData, setDrillDownData] = useState(null);
  const [drillDownLoading, setDrillDownLoading] = useState(false);
  const [selectedChildForDetail, setSelectedChildForDetail] = useState(null);
  
  // Support tools state
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [usersLoading, setUsersLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [showResetConfirm, setShowResetConfirm] = useState(null); // 'password' | 'pin'
  const [resetResult, setResetResult] = useState(null);
  const [planChangeLoading, setPlanChangeLoading] = useState(null); // userId being changed
  
  // Traffic analytics state
  const [trafficData, setTrafficData] = useState(null);
  const [trafficLoading, setTrafficLoading] = useState(false);
  const [excludeAdminTraffic, setExcludeAdminTraffic] = useState(true); // Default to exclude admin/test

  // Fetch traffic data when filter changes
  const fetchTrafficData = async (excludeAdmin = true) => {
    try {
      setTrafficLoading(true);
      const trafficRes = await api.get(`/admin/analytics/traffic?days=30&exclude_admin=${excludeAdmin}`);
      setTrafficData(trafficRes.data);
    } catch (err) {
      console.log('Traffic analytics fetch failed:', err);
    } finally {
      setTrafficLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedChild) {
      fetchChildAnalytics(selectedChild.id);
    }
  }, [selectedChild]);

  const fetchData = async () => {
    try {
      // Check if user is admin
      try {
        const adminRes = await betaAPI.checkAdmin();
        setIsAdmin(adminRes.data.is_admin);
        
        // If admin, fetch beta invites and feedback
        if (adminRes.data.is_admin) {
          const invitesRes = await betaAPI.getInvites();
          setBetaInvites(invitesRes.data || []);
          
          // Fetch beta feedback
          try {
            const feedbackRes = await api.get('/admin/feedback');
            setBetaFeedback(feedbackRes.data || []);
          } catch (err) {
            console.log('Feedback fetch failed:', err);
          }
          
          // Fetch traffic analytics
          fetchTrafficData(true); // Default to exclude admin traffic
        }
      } catch (err) {
        console.log('Admin check failed:', err);
      }
      
      // Fetch children for parent view
      const childrenRes = await childrenAPI.getAll();
      setChildren(childrenRes.data || []);
      if (childrenRes.data?.length > 0) {
        setSelectedChild(childrenRes.data[0]);
      }
      
      // Fetch platform analytics
      try {
        const platformRes = await analyticsAPI.getPlatformAnalytics();
        setPlatformAnalytics(platformRes.data);
      } catch (err) {
        console.log('Platform analytics not available');
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const fetchChildAnalytics = async (childId) => {
    try {
      const res = await analyticsAPI.getChildAnalytics(childId);
      setChildAnalytics(res.data);
    } catch (error) {
      console.error('Error fetching child analytics:', error);
    }
  };
  
  // Beta Invite Functions
  const createBetaInvite = async () => {
    if (!newInvite.parent_name || !newInvite.parent_email) {
      toast.error('Please fill in parent name and email');
      return;
    }
    
    setInviteLoading(true);
    try {
      const res = await betaAPI.createInvite({
        parent_name: newInvite.parent_name,
        parent_email: newInvite.parent_email,
        notes: newInvite.notes || null,
        expires_in_days: 14
      });
      setBetaInvites([res.data, ...betaInvites]);
      setNewInvite({ parent_name: '', parent_email: '', notes: '' });
      setShowInviteModal(false);
      toast.success('Beta invite created!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invite');
    } finally {
      setInviteLoading(false);
    }
  };
  
  const copyInviteLink = (invite) => {
    navigator.clipboard.writeText(invite.invite_link);
    toast.success('Invite link copied to clipboard!');
  };
  
  // Open email template modal for sending invite
  const openSendEmailModal = (invite) => {
    setSelectedInviteForEmail(invite);
    setShowEmailModal(true);
  };
  
  // Generate email template with invite link
  const generateEmailTemplate = (invite) => {
    return `Hello,

We're excited to invite your family to help test Temple of Children, a new interactive learning world designed to help children grow through curiosity, creativity, and exploration.

Temple of Children was created by our family after years of searching for learning tools that truly inspire children. We wanted to build something different — a place where learning feels like an adventure instead of a chore.

Inside the Temple, children explore a magical world where they can:

• earn stars by completing lessons
• solve puzzles and challenges
• learn strategy in the Chess Garden
• practice skills like typing and logic
• create their own explorer avatar
• discover new knowledge across different realms

Our mission is simple:

To help children fall in love with learning by turning education into an exciting journey of discovery.

We are currently preparing for our first family beta test, and we're inviting a small group of parents and children to explore the Temple and share their feedback as we continue building the platform.

If you'd like to participate, please use your private beta invitation link below:

${invite.invite_link}

Once you create your account, you can set up your child's explorer profile and begin exploring the Temple together.

Your feedback will directly help shape the future of Temple of Children as we continue adding new lessons, games, and creative learning experiences.

Why we built this:

We believe children learn best when they are curious, creative, and encouraged to explore the world around them. Temple of Children is our attempt to build a learning environment that makes education feel magical and meaningful.

Thank you for helping us build something meaningful for children.

Warmly,
Temple of Children
toclearning.com`;
  };
  
  const copyEmailTemplate = () => {
    if (selectedInviteForEmail) {
      navigator.clipboard.writeText(generateEmailTemplate(selectedInviteForEmail));
      toast.success('Email template copied to clipboard!');
    }
  };
  
  const sendAndMarkSent = async () => {
    if (selectedInviteForEmail) {
      await markInviteSent(selectedInviteForEmail.id);
      setShowEmailModal(false);
      setSelectedInviteForEmail(null);
    }
  };
  
  const markInviteSent = async (inviteId) => {
    try {
      await betaAPI.markSent(inviteId);
      setBetaInvites(betaInvites.map(inv => 
        inv.id === inviteId ? { ...inv, status: 'sent', sent_at: new Date().toISOString() } : inv
      ));
      toast.success('Invite marked as sent');
    } catch (error) {
      toast.error('Failed to update invite');
    }
  };
  
  const revokeInvite = async (inviteId) => {
    try {
      await betaAPI.revokeInvite(inviteId);
      setBetaInvites(betaInvites.map(inv => 
        inv.id === inviteId ? { ...inv, status: 'revoked' } : inv
      ));
      toast.success('Invite revoked');
    } catch (error) {
      toast.error('Failed to revoke invite');
    }
  };
  
  const deleteInvite = async (inviteId) => {
    try {
      await betaAPI.deleteInvite(inviteId);
      setBetaInvites(betaInvites.filter(inv => inv.id !== inviteId));
      toast.success('Invite deleted');
    } catch (error) {
      toast.error('Failed to delete invite');
    }
  };
  
  const refreshInvites = async () => {
    try {
      const invitesRes = await betaAPI.getInvites();
      setBetaInvites(invitesRes.data || []);
      toast.success('Invites refreshed');
    } catch (error) {
      toast.error('Failed to refresh invites');
    }
  };
  
  const getStatusBadge = (status) => {
    const statusStyles = {
      pending: 'bg-slate-500/20 text-slate-300',
      sent: 'bg-blue-500/20 text-blue-300',
      opened: 'bg-cyan-500/20 text-cyan-300',
      registered: 'bg-green-500/20 text-green-300',
      active: 'bg-emerald-500/20 text-emerald-300',
      revoked: 'bg-red-500/20 text-red-300',
      expired: 'bg-orange-500/20 text-orange-300'
    };
    return statusStyles[status] || statusStyles.pending;
  };
  
  // ============ DRILL-DOWN FUNCTIONS ============
  
  const openDrillDown = async (type) => {
    setDrillDownModal(type);
    setDrillDownLoading(true);
    setDrillDownData(null);
    
    try {
      let res;
      switch (type) {
        case 'activity':
          res = await adminAPI.getActivityUsage();
          break;
        case 'learning':
          res = await adminAPI.getLearningAnalytics();
          break;
        case 'creative':
          res = await adminAPI.getCreativeAnalytics();
          break;
        case 'reading':
          res = await adminAPI.getReadingAnalytics();
          break;
        default:
          break;
      }
      setDrillDownData(res?.data);
    } catch (error) {
      toast.error('Failed to load details');
      console.error(error);
    } finally {
      setDrillDownLoading(false);
    }
  };
  
  const openChildDetail = async (childId) => {
    setSelectedChildForDetail(childId);
    setDrillDownModal('child');
    setDrillDownLoading(true);
    
    try {
      const res = await adminAPI.getChildActivity(childId);
      setDrillDownData(res.data);
    } catch (error) {
      toast.error('Failed to load child details');
    } finally {
      setDrillDownLoading(false);
    }
  };
  
  const closeDrillDown = () => {
    setDrillDownModal(null);
    setDrillDownData(null);
    setSelectedChildForDetail(null);
  };
  
  // ============ SUPPORT TOOLS FUNCTIONS ============
  
  const searchUsers = async () => {
    setUsersLoading(true);
    try {
      const res = await adminAPI.getUsers(userSearch || null);
      setUsers(res.data || []);
    } catch (error) {
      toast.error('Failed to search users');
    } finally {
      setUsersLoading(false);
    }
  };
  
  const openUserDetail = async (userId) => {
    setSelectedUser(userId);
    try {
      const res = await adminAPI.getUserDetail(userId);
      setUserDetail(res.data);
    } catch (error) {
      toast.error('Failed to load user details');
    }
  };
  
  const handleResetPassword = async (userId) => {
    try {
      const res = await adminAPI.resetPassword(userId);
      setResetResult(res.data);
      toast.success('Password reset successful');
      setShowResetConfirm(null);
    } catch (error) {
      toast.error('Failed to reset password');
    }
  };
  
  const handleResetPin = async (childId) => {
    try {
      const res = await adminAPI.resetChildPin(childId);
      setResetResult(res.data);
      toast.success('PIN reset successful');
      setShowResetConfirm(null);
    } catch (error) {
      toast.error('Failed to reset PIN');
    }
  };

  // Handle plan change for a user
  const handlePlanChange = async (userId, newPlan) => {
    setPlanChangeLoading(userId);
    try {
      await adminAPI.setUserPlan(userId, newPlan);
      toast.success(`Plan updated to ${newPlan}`);
      
      // Update the user in the local state
      setUsers(users.map(u => 
        u.id === userId ? { ...u, current_plan: newPlan } : u
      ));
      
      // Also update userDetail if viewing this user
      if (userDetail?.user?.id === userId) {
        setUserDetail({
          ...userDetail,
          user: { ...userDetail.user, current_plan: newPlan }
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update plan');
    } finally {
      setPlanChangeLoading(null);
    }
  };

  // Get plan badge styling
  const getPlanBadge = (plan) => {
    switch (plan) {
      case 'founder':
        return 'bg-amber-500/20 text-amber-300 border-amber-500/30';
      case 'family':
        return 'bg-indigo-500/20 text-indigo-300 border-indigo-500/30';
      case 'lifetime':
        return 'bg-purple-500/20 text-purple-300 border-purple-500/30';
      default:
        return 'bg-slate-500/20 text-slate-300 border-slate-500/30';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0F172A] flex items-center justify-center">
        <div className="text-white text-xl">Loading Analytics...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0F172A] text-white">
      {/* Header */}
      <header className="border-b border-white/10 bg-slate-900/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/dashboard')}
              className="text-slate-400 hover:text-white"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-xl font-philosopher font-bold">Analytics Dashboard</h1>
          </div>
          
          {/* View Toggle */}
          <div className="flex items-center gap-2 bg-slate-800/50 rounded-lg p-1">
            <button
              onClick={() => setView('parent')}
              className={`px-4 py-2 rounded-lg text-sm transition-all ${
                view === 'parent' ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-white'
              }`}
              data-testid="parent-view-btn"
            >
              Parent View
            </button>
            <button
              onClick={() => setView('admin')}
              className={`px-4 py-2 rounded-lg text-sm transition-all ${
                view === 'admin' ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-white'
              }`}
              data-testid="admin-view-btn"
            >
              Admin View
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {view === 'parent' ? (
          /* Parent View */
          <div className="space-y-8">
            {/* Child Selector */}
            {children.length > 1 && (
              <div className="flex items-center gap-3">
                <span className="text-slate-400">Select child:</span>
                <div className="flex gap-2">
                  {children.map(child => (
                    <button
                      key={child.id}
                      onClick={() => setSelectedChild(child)}
                      className={`px-4 py-2 rounded-lg transition-all ${
                        selectedChild?.id === child.id
                          ? 'bg-indigo-600 text-white'
                          : 'bg-slate-800/50 text-slate-300 hover:bg-slate-700/50'
                      }`}
                    >
                      {child.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {/* Child Analytics */}
            {childAnalytics && selectedChild && (
              <ChildAnalyticsCard analytics={childAnalytics} childName={selectedChild.name} />
            )}
            
            {/* Weekly Summary */}
            <div className="bg-slate-800/30 border border-slate-700/50 rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-blue-400" />
                This Week
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <StatCard
                  icon={BookOpen}
                  label="Lessons Completed"
                  value={childAnalytics?.lessons_completed_week || 0}
                  subValue="this week"
                  color="green"
                />
                <StatCard
                  icon={Flame}
                  label="Current Streak"
                  value={`${childAnalytics?.current_streak || 0} days`}
                  subValue="keep it going!"
                  color="orange"
                />
                <StatCard
                  icon={Sparkles}
                  label="Total Sparks"
                  value={childAnalytics?.total_sparks || 0}
                  subValue="earned so far"
                  color="yellow"
                />
              </div>
            </div>
            
            {/* Recommended Lessons */}
            {childAnalytics?.recommended_lessons?.length > 0 && (
              <div className="bg-slate-800/30 border border-slate-700/50 rounded-2xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Star className="w-5 h-5 text-yellow-400" />
                  Recommended Next
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {childAnalytics.recommended_lessons.slice(0, 3).map((lesson, i) => (
                    <div key={i} className="p-4 bg-slate-900/50 rounded-xl border border-slate-700/30">
                      <div className="font-medium text-white">{lesson.title}</div>
                      <div className="text-xs text-slate-400 mt-1">{lesson.room_id}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          /* Admin View */
          <div className="space-y-8">
            {/* Admin Tabs */}
            <div className="flex items-center gap-2 bg-slate-800/30 rounded-lg p-1 w-fit">
              <button
                onClick={() => setAdminTab('overview')}
                className={`px-4 py-2 rounded-lg text-sm transition-all flex items-center gap-2 ${
                  adminTab === 'overview' ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-white'
                }`}
                data-testid="admin-overview-tab"
              >
                <BarChart3 className="w-4 h-4" />
                Analytics Overview
              </button>
              <button
                onClick={() => { setAdminTab('users'); searchUsers(); }}
                className={`px-4 py-2 rounded-lg text-sm transition-all flex items-center gap-2 ${
                  adminTab === 'users' ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-white'
                }`}
                data-testid="admin-users-tab"
              >
                <Users className="w-4 h-4" />
                User Management
              </button>
            </div>
            
            {adminTab === 'overview' ? (
              <>
                {/* Platform Overview - Clickable Tiles */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <StatCard
                    icon={Activity}
                    label="Activity Usage"
                    value={platformAnalytics?.daily_active_users || 0}
                    subValue="active today - click for details"
                    color="green"
                    clickable
                    onClick={() => openDrillDown('activity')}
                  />
                  <StatCard
                    icon={BookOpen}
                    label="Learning"
                    value={platformAnalytics?.lessons_completed_today || 0}
                    subValue="lessons today - click for details"
                    color="blue"
                    clickable
                    onClick={() => openDrillDown('learning')}
                  />
                  <StatCard
                    icon={Music}
                    label="Creative Tools"
                    value={(platformAnalytics?.total_beats || 0) + (platformAnalytics?.total_songs || 0)}
                    subValue="creations - click for details"
                    color="fuchsia"
                    clickable
                    onClick={() => openDrillDown('creative')}
                  />
                  <StatCard
                    icon={Book}
                    label="Reading"
                    value={platformAnalytics?.reading_sessions || 0}
                    subValue="Temple Listens - click for details"
                    color="amber"
                    clickable
                    onClick={() => openDrillDown('reading')}
                  />
                </div>
                
                {/* Website Traffic Analytics - Moved up for visibility */}
                <div className="bg-slate-800/30 border border-slate-700/50 rounded-2xl p-4 sm:p-6">
                  <div className="flex items-center justify-between mb-3 sm:mb-4">
                    <h3 className="text-base sm:text-lg font-semibold text-white flex items-center gap-2">
                      <Eye className="w-5 h-5 text-cyan-400" />
                      Website Traffic (Last 30 Days)
                    </h3>
                    {/* Toggle for excluding admin/test traffic */}
                    <label className="flex items-center gap-2 cursor-pointer">
                      <span className="text-xs text-slate-400">Hide admin/test</span>
                      <div 
                        className={`relative w-10 h-5 rounded-full transition-colors ${excludeAdminTraffic ? 'bg-cyan-500' : 'bg-slate-600'}`}
                        onClick={() => {
                          const newValue = !excludeAdminTraffic;
                          setExcludeAdminTraffic(newValue);
                          fetchTrafficData(newValue);
                        }}
                      >
                        <div 
                          className={`absolute w-4 h-4 rounded-full bg-white shadow top-0.5 transition-all ${excludeAdminTraffic ? 'left-5' : 'left-0.5'}`} 
                        />
                      </div>
                    </label>
                  </div>
                  {/* Filter status note */}
                  {excludeAdminTraffic && (
                    <p className="text-xs text-slate-500 mb-2">
                      Filtering signups only (page views filter applies to new visits)
                    </p>
                  )}
                  {trafficLoading ? (
                    <div className="text-center py-4 text-slate-400">Loading traffic data...</div>
                  ) : trafficData ? (
                    <div className="space-y-3 sm:space-y-4">
                      {/* Traffic Overview Stats */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
                        <div className="bg-slate-900/50 rounded-xl p-3 sm:p-4 text-center">
                          <div className="text-xl sm:text-2xl font-bold text-cyan-400">{trafficData.today?.page_views || 0}</div>
                          <div className="text-xs text-slate-400">Views Today</div>
                        </div>
                        <div className="bg-slate-900/50 rounded-xl p-3 sm:p-4 text-center">
                          <div className="text-xl sm:text-2xl font-bold text-green-400">{trafficData.today?.unique_visitors || 0}</div>
                          <div className="text-xs text-slate-400">Unique Today</div>
                        </div>
                        <div className="bg-slate-900/50 rounded-xl p-3 sm:p-4 text-center">
                          <div className="text-xl sm:text-2xl font-bold text-purple-400">{trafficData.total_page_views || 0}</div>
                          <div className="text-xs text-slate-400">Total Views (30d)</div>
                        </div>
                        <div className="bg-slate-900/50 rounded-xl p-3 sm:p-4 text-center">
                          <div className="text-xl sm:text-2xl font-bold text-amber-400">{trafficData.total_unique_visitors || 0}</div>
                          <div className="text-xs text-slate-400">Unique Visitors (30d)</div>
                        </div>
                      </div>
                      
                      {/* Conversion Rate */}
                      <div className="flex items-center justify-between bg-slate-900/50 rounded-xl p-3 sm:p-4">
                        <div className="flex items-center gap-2 sm:gap-3">
                          <UserPlus className="w-4 h-4 sm:w-5 sm:h-5 text-green-400" />
                          <div>
                            <div className="text-white font-medium text-sm sm:text-base">Visitor → Signup</div>
                            <div className="text-xs text-slate-400">
                              {trafficData.signups_in_period || 0} signups from {trafficData.total_unique_visitors || 0} visitors
                              {trafficData.test_accounts_excluded > 0 && (
                                <span className="text-slate-500"> ({trafficData.test_accounts_excluded} test excluded)</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="text-xl sm:text-2xl font-bold text-green-400">{trafficData.conversion_rate || 0}%</div>
                      </div>
                      
                      {/* Top Pages - Collapsible on mobile */}
                      {trafficData.top_pages?.length > 0 && (
                        <div>
                          <h4 className="text-sm font-medium text-slate-300 mb-2">Top Pages</h4>
                          <div className="space-y-1 sm:space-y-2">
                            {trafficData.top_pages.slice(0, 5).map((page, idx) => (
                              <div key={idx} className="flex items-center justify-between text-xs sm:text-sm bg-slate-900/30 rounded-lg px-2 sm:px-3 py-1.5 sm:py-2">
                                <span className="text-slate-300 truncate max-w-[40%] sm:max-w-[60%]">{page.page || '/'}</span>
                                <div className="flex items-center gap-2 sm:gap-3">
                                  <span className="text-cyan-400">{page.views} views</span>
                                  <span className="text-slate-500 hidden sm:inline">({page.unique_visitors} unique)</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Daily Traffic Chart */}
                      {trafficData.daily_traffic?.length > 0 && (
                        <div>
                          <h4 className="text-sm font-medium text-slate-300 mb-2">Daily Traffic (Last 7 Days)</h4>
                          <div className="space-y-1">
                            {trafficData.daily_traffic.slice(0, 7).map((day, idx) => {
                              const maxViews = Math.max(...trafficData.daily_traffic.slice(0, 7).map(d => d.total_views || 1));
                              return (
                                <div key={idx} className="flex items-center gap-2 text-xs sm:text-sm">
                                  <span className="w-12 sm:w-20 text-slate-500">{day.date?.slice(5) || ''}</span>
                                  <div className="flex-1 h-4 sm:h-5 bg-slate-900/50 rounded overflow-hidden">
                                    <div 
                                      className="h-full bg-gradient-to-r from-cyan-500 to-blue-500 rounded transition-all"
                                      style={{ width: `${((day.total_views || 0) / maxViews) * 100}%` }}
                                    />
                                  </div>
                                  <span className="w-10 sm:w-16 text-right text-slate-400">{day.total_views || 0}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-4 text-slate-500">No traffic data available yet</div>
                  )}
                </div>
                
                {/* Summary Stats Row - Now Clickable */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <StatCard
                    icon={Users}
                    label="Total Users"
                    value={platformAnalytics?.total_users || 0}
                    subValue={`${platformAnalytics?.total_children || 0} children - click to manage`}
                    color="indigo"
                    clickable
                    onClick={() => { setAdminTab('users'); searchUsers(); }}
                  />
                  <StatCard
                    icon={Clock}
                    label="Avg Session"
                    value={`${platformAnalytics?.average_session_minutes || 0} min`}
                    subValue="click for time breakdown"
                    color="cyan"
                    clickable
                    onClick={() => openDrillDown('activity')}
                  />
                  <StatCard
                    icon={Award}
                    label="Completion Rate"
                    value={`${platformAnalytics?.completion_rate || 0}%`}
                    subValue="click for lesson details"
                    color="yellow"
                    clickable
                    onClick={() => openDrillDown('learning')}
                  />
                  <StatCard
                    icon={Flame}
                    label="Weekly Active"
                    value={platformAnalytics?.weekly_active_users || 0}
                    subValue="click to see active users"
                    color="orange"
                    clickable
                    onClick={() => openDrillDown('activity')}
                  />
                </div>
            
                {/* Lessons Today */}
                <div className="bg-slate-800/30 border border-slate-700/50 rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-green-400" />
                Lessons Today
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <ProgressBar 
                    label="Started" 
                    value={platformAnalytics?.lessons_started_today || 0} 
                    max={Math.max(platformAnalytics?.lessons_started_today || 1, 100)} 
                    color="blue"
                  />
                  <ProgressBar 
                    label="Completed" 
                    value={platformAnalytics?.lessons_completed_today || 0} 
                    max={platformAnalytics?.lessons_started_today || 1} 
                    color="green"
                  />
                </div>
                <div className="flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-4xl font-bold text-white">
                      {platformAnalytics?.completion_rate || 0}%
                    </div>
                    <div className="text-slate-400">completion rate</div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Popular Realms */}
            <div className="bg-slate-800/30 border border-slate-700/50 rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-purple-400" />
                Popular Realms
              </h3>
              <div className="space-y-3">
                {Object.entries(platformAnalytics?.popular_realms || {}).map(([realm, count]) => {
                  const maxCount = Math.max(...Object.values(platformAnalytics?.popular_realms || {}), 1);
                  return (
                    <div key={realm} className="flex items-center gap-4">
                      <div className="w-32 text-sm text-slate-300 capitalize">{realm.replace('_', ' ')}</div>
                      <div className="flex-1 h-6 bg-slate-700/50 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full"
                          style={{ width: `${(count / maxCount) * 100}%` }}
                        />
                      </div>
                      <div className="w-12 text-right text-slate-400">{count}</div>
                    </div>
                  );
                })}
                {Object.keys(platformAnalytics?.popular_realms || {}).length === 0 && (
                  <p className="text-slate-500 text-center py-4">No realm activity data yet</p>
                )}
              </div>
            </div>
            
            {/* Difficult Lessons */}
            {platformAnalytics?.difficult_lessons?.length > 0 && (
              <div className="bg-slate-800/30 border border-slate-700/50 rounded-2xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-orange-400" />
                  Difficult Lessons (Low Completion)
                </h3>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="text-left text-sm text-slate-400 border-b border-slate-700/50">
                        <th className="pb-3">Lesson</th>
                        <th className="pb-3">Started</th>
                        <th className="pb-3">Completed</th>
                        <th className="pb-3">Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {platformAnalytics.difficult_lessons.map((lesson, i) => (
                        <tr key={i} className="border-b border-slate-700/30">
                          <td className="py-3 text-white">{lesson.title}</td>
                          <td className="py-3 text-slate-400">{lesson.starts}</td>
                          <td className="py-3 text-slate-400">{lesson.completions}</td>
                          <td className="py-3">
                            <span className={`px-2 py-1 rounded text-xs ${
                              lesson.completion_rate < 30 ? 'bg-red-500/20 text-red-400' :
                              lesson.completion_rate < 50 ? 'bg-orange-500/20 text-orange-400' :
                              'bg-yellow-500/20 text-yellow-400'
                            }`}>
                              {lesson.completion_rate}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            
            {/* Legacy Beta Invites Section - Collapsible */}
            {isAdmin && (
              <div className="bg-slate-800/30 border border-slate-700/50 rounded-2xl overflow-hidden">
                <button
                  onClick={() => setShowBetaInvites(!showBetaInvites)}
                  className="w-full p-4 flex items-center justify-between hover:bg-slate-700/20 transition-all"
                >
                  <h3 className="text-sm font-medium text-slate-400 flex items-center gap-2">
                    <Mail className="w-4 h-4 text-slate-500" />
                    Legacy Beta Invites ({betaInvites.length})
                  </h3>
                  <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${showBetaInvites ? 'rotate-180' : ''}`} />
                </button>
                
                {showBetaInvites && (
                  <div className="p-6 pt-0">
                    <div className="flex items-center justify-end mb-4 gap-2">
                      <button
                        onClick={refreshInvites}
                        className="p-2 bg-slate-700/50 hover:bg-slate-600/50 rounded-lg transition-all"
                        data-testid="refresh-invites-btn"
                      >
                        <RefreshCw className="w-4 h-4 text-slate-300" />
                      </button>
                      <button
                        onClick={() => setShowInviteModal(true)}
                        className="flex items-center gap-2 px-3 py-2 bg-slate-700/50 hover:bg-slate-600/50 rounded-lg transition-all text-sm"
                        data-testid="create-invite-btn"
                      >
                        <UserPlus className="w-4 h-4" />
                        New Invite
                      </button>
                    </div>
                
                {/* Invite Stats */}
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
                  <div className="text-center p-3 bg-slate-900/50 rounded-xl">
                    <div className="text-xl font-bold text-white">{betaInvites.length}</div>
                    <div className="text-xs text-slate-400">Total</div>
                  </div>
                  <div className="text-center p-3 bg-slate-900/50 rounded-xl">
                    <div className="text-xl font-bold text-blue-400">{betaInvites.filter(i => i.status === 'sent').length}</div>
                    <div className="text-xs text-slate-400">Sent</div>
                  </div>
                  <div className="text-center p-3 bg-slate-900/50 rounded-xl">
                    <div className="text-xl font-bold text-cyan-400">{betaInvites.filter(i => i.status === 'opened').length}</div>
                    <div className="text-xs text-slate-400">Opened</div>
                  </div>
                  <div className="text-center p-3 bg-slate-900/50 rounded-xl">
                    <div className="text-xl font-bold text-green-400">{betaInvites.filter(i => ['registered', 'active'].includes(i.status)).length}</div>
                    <div className="text-xs text-slate-400">Registered</div>
                  </div>
                  <div className="text-center p-3 bg-slate-900/50 rounded-xl">
                    <div className="text-xl font-bold text-red-400">{betaInvites.filter(i => ['revoked', 'expired'].includes(i.status)).length}</div>
                    <div className="text-xs text-slate-400">Inactive</div>
                  </div>
                </div>
                
                {/* Invites Table */}
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="text-left text-sm text-slate-400 border-b border-slate-700/50">
                        <th className="pb-3">Family</th>
                        <th className="pb-3">Code</th>
                        <th className="pb-3">Status</th>
                        <th className="pb-3">Created</th>
                        <th className="pb-3">Expires</th>
                        <th className="pb-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {betaInvites.map(invite => (
                        <tr key={invite.id} className="border-b border-slate-700/30 hover:bg-slate-700/20">
                          <td className="py-3">
                            <div className="text-white font-medium">{invite.parent_name}</div>
                            <div className="text-xs text-slate-400">{invite.parent_email}</div>
                            {invite.notes && <div className="text-xs text-slate-500 mt-1">{invite.notes}</div>}
                          </td>
                          <td className="py-3">
                            <code className="bg-slate-900 px-2 py-1 rounded text-cyan-400 text-sm">
                              {invite.invite_code}
                            </code>
                          </td>
                          <td className="py-3">
                            <span className={`px-2 py-1 rounded text-xs capitalize ${getStatusBadge(invite.status)}`}>
                              {invite.status}
                            </span>
                          </td>
                          <td className="py-3 text-sm text-slate-400">
                            {new Date(invite.created_at).toLocaleDateString()}
                          </td>
                          <td className="py-3 text-sm text-slate-400">
                            {new Date(invite.expires_at).toLocaleDateString()}
                          </td>
                          <td className="py-3">
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => copyInviteLink(invite)}
                                className="p-1.5 bg-slate-700/50 hover:bg-cyan-600/50 rounded transition-all"
                                title="Copy invite link"
                              >
                                <Copy className="w-3.5 h-3.5 text-slate-300" />
                              </button>
                              {invite.status === 'pending' && (
                                <button
                                  onClick={() => openSendEmailModal(invite)}
                                  className="p-1.5 bg-slate-700/50 hover:bg-blue-600/50 rounded transition-all"
                                  title="Send invite email"
                                  data-testid={`send-invite-${invite.id}`}
                                >
                                  <Send className="w-3.5 h-3.5 text-slate-300" />
                                </button>
                              )}
                              {!['revoked', 'registered', 'active'].includes(invite.status) && (
                                <button
                                  onClick={() => revokeInvite(invite.id)}
                                  className="p-1.5 bg-slate-700/50 hover:bg-red-600/50 rounded transition-all"
                                  title="Revoke invite"
                                >
                                  <X className="w-3.5 h-3.5 text-slate-300" />
                                </button>
                              )}
                              <button
                                onClick={() => deleteInvite(invite.id)}
                                className="p-1.5 bg-slate-700/50 hover:bg-red-600/50 rounded transition-all"
                                title="Delete invite"
                              >
                                <Trash2 className="w-3.5 h-3.5 text-slate-300" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {betaInvites.length === 0 && (
                        <tr>
                          <td colSpan="6" className="py-8 text-center text-slate-500">
                            No beta invites yet. Click "New Invite" to create one.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                </div>
                )}
              </div>
            )}
            
            {/* Beta Feedback Section - Admin Only */}
            {isAdmin && (
              <div className="bg-slate-800/30 border border-slate-700/50 rounded-2xl p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-purple-400" />
                    Beta Feedback
                  </h3>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={async () => {
                        try {
                          const feedbackRes = await api.get('/admin/feedback');
                          setBetaFeedback(feedbackRes.data || []);
                          toast.success('Feedback refreshed');
                        } catch (err) {
                          toast.error('Could not refresh feedback');
                        }
                      }}
                      className="p-2 bg-slate-700/50 hover:bg-slate-600/50 rounded-lg transition-all"
                      data-testid="refresh-feedback-btn"
                    >
                      <RefreshCw className="w-4 h-4 text-slate-300" />
                    </button>
                  </div>
                </div>
                
                {/* Feedback Stats */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                  <div className="text-center p-3 bg-slate-900/50 rounded-xl">
                    <div className="text-xl font-bold text-red-400">{betaFeedback.filter(f => f.feedback_type === 'bug').length}</div>
                    <div className="text-xs text-slate-400 flex items-center justify-center gap-1"><Bug className="w-3 h-3" /> Bugs</div>
                  </div>
                  <div className="text-center p-3 bg-slate-900/50 rounded-xl">
                    <div className="text-xl font-bold text-amber-400">{betaFeedback.filter(f => f.feedback_type === 'confusing').length}</div>
                    <div className="text-xs text-slate-400 flex items-center justify-center gap-1"><HelpCircle className="w-3 h-3" /> Confusing</div>
                  </div>
                  <div className="text-center p-3 bg-slate-900/50 rounded-xl">
                    <div className="text-xl font-bold text-cyan-400">{betaFeedback.filter(f => f.feedback_type === 'idea').length}</div>
                    <div className="text-xs text-slate-400 flex items-center justify-center gap-1"><Lightbulb className="w-3 h-3" /> Ideas</div>
                  </div>
                  <div className="text-center p-3 bg-slate-900/50 rounded-xl">
                    <div className="text-xl font-bold text-pink-400">{betaFeedback.filter(f => f.feedback_type === 'praise').length}</div>
                    <div className="text-xs text-slate-400 flex items-center justify-center gap-1"><Heart className="w-3 h-3" /> Praise</div>
                  </div>
                </div>
                
                {/* Feedback List */}
                <div className="space-y-3 max-h-[400px] overflow-y-auto">
                  {betaFeedback.map(feedback => {
                    const typeConfig = {
                      bug: { icon: Bug, color: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/20' },
                      confusing: { icon: HelpCircle, color: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/20' },
                      idea: { icon: Lightbulb, color: 'text-cyan-400', bg: 'bg-cyan-500/10', border: 'border-cyan-500/20' },
                      praise: { icon: Heart, color: 'text-pink-400', bg: 'bg-pink-500/10', border: 'border-pink-500/20' }
                    }[feedback.feedback_type] || { icon: MessageSquare, color: 'text-slate-400', bg: 'bg-slate-500/10', border: 'border-slate-500/20' };
                    const TypeIcon = typeConfig.icon;
                    
                    return (
                      <div 
                        key={feedback.id} 
                        className={`${typeConfig.bg} border ${typeConfig.border} rounded-xl p-4`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <TypeIcon className={`w-4 h-4 ${typeConfig.color}`} />
                              <span className={`text-sm font-medium capitalize ${typeConfig.color}`}>
                                {feedback.feedback_type}
                              </span>
                              <span className="text-slate-500">•</span>
                              <span className="text-xs text-slate-500">
                                {new Date(feedback.created_at).toLocaleString()}
                              </span>
                            </div>
                            <p className="text-white text-sm mb-2">{feedback.message}</p>
                            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                              <span>📧 {feedback.parent_email}</span>
                              {feedback.child_name && <span>👤 {feedback.child_name}</span>}
                              {feedback.page_route && <span>📍 {feedback.page_route}</span>}
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <span className={`px-2 py-1 rounded text-xs ${
                              feedback.status === 'new' ? 'bg-blue-500/20 text-blue-400' :
                              feedback.status === 'reviewed' ? 'bg-amber-500/20 text-amber-400' :
                              feedback.status === 'resolved' ? 'bg-green-500/20 text-green-400' :
                              'bg-slate-500/20 text-slate-400'
                            }`}>
                              {feedback.status}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {betaFeedback.length === 0 && (
                    <div className="text-center py-8 text-slate-500">
                      <MessageSquare className="w-12 h-12 mx-auto mb-2 opacity-30" />
                      <p>No feedback yet</p>
                      <p className="text-xs mt-1">Beta users can submit feedback using the Feedback button</p>
                    </div>
                  )}
                </div>
              </div>
            )}
              </>
            ) : (
              /* User Management Tab */
              <div className="space-y-6">
                {/* Test Email Filter Toggle */}
                <div className="flex items-center justify-between mb-4 p-3 bg-slate-800/50 rounded-xl border border-slate-700/50">
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-slate-300">
                      Exclude test/demo emails
                    </label>
                    <span className="text-xs text-slate-500">
                      ({users.filter(u => isTestEmail(u.email)).length} test accounts hidden)
                    </span>
                  </div>
                  <button
                    onClick={() => setExcludeTestEmails(!excludeTestEmails)}
                    className={`relative w-12 h-6 rounded-full transition-colors ${
                      excludeTestEmails ? 'bg-green-600' : 'bg-slate-600'
                    }`}
                    data-testid="exclude-test-toggle"
                  >
                    <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                      excludeTestEmails ? 'translate-x-7' : 'translate-x-1'
                    }`} />
                  </button>
                </div>

                {/* User Stats Summary */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div 
                    onClick={() => setUserTierFilter('all')}
                    className={`p-4 rounded-xl border cursor-pointer transition-all ${
                      userTierFilter === 'all' 
                        ? 'bg-slate-700/50 border-slate-500' 
                        : 'bg-slate-800/30 border-slate-700/50 hover:border-slate-600'
                    }`}
                  >
                    <div className="text-2xl font-bold text-white">
                      {users.filter(u => !excludeTestEmails || !isTestEmail(u.email)).length}
                    </div>
                    <div className="text-sm text-slate-400">
                      {excludeTestEmails ? 'Real Users' : 'All Users'}
                    </div>
                  </div>
                  <div 
                    onClick={() => setUserTierFilter('free')}
                    className={`p-4 rounded-xl border cursor-pointer transition-all ${
                      userTierFilter === 'free' 
                        ? 'bg-slate-700/50 border-slate-500' 
                        : 'bg-slate-800/30 border-slate-700/50 hover:border-slate-600'
                    }`}
                  >
                    <div className="text-2xl font-bold text-slate-300">
                      {users.filter(u => u.current_plan === 'free' && (!excludeTestEmails || !isTestEmail(u.email))).length}
                    </div>
                    <div className="text-sm text-slate-400">Free Tier</div>
                  </div>
                  <div 
                    onClick={() => setUserTierFilter('family')}
                    className={`p-4 rounded-xl border cursor-pointer transition-all ${
                      userTierFilter === 'family' 
                        ? 'bg-indigo-600/30 border-indigo-500' 
                        : 'bg-slate-800/30 border-slate-700/50 hover:border-indigo-500/50'
                    }`}
                  >
                    <div className="text-2xl font-bold text-indigo-300">
                      {users.filter(u => u.current_plan === 'family' && (!excludeTestEmails || !isTestEmail(u.email))).length}
                    </div>
                    <div className="text-sm text-slate-400">Family Plan</div>
                  </div>
                  <div 
                    onClick={() => setUserTierFilter('founder')}
                    className={`p-4 rounded-xl border cursor-pointer transition-all ${
                      userTierFilter === 'founder' 
                        ? 'bg-amber-600/30 border-amber-500' 
                        : 'bg-slate-800/30 border-slate-700/50 hover:border-amber-500/50'
                    }`}
                  >
                    <div className="text-2xl font-bold text-amber-300">
                      {users.filter(u => u.current_plan === 'founder' && (!excludeTestEmails || !isTestEmail(u.email))).length}
                    </div>
                    <div className="text-sm text-slate-400">Founders</div>
                  </div>
                </div>

                {/* User Search & Management */}
                <div className="bg-slate-800/30 border border-slate-700/50 rounded-2xl p-6">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <Users className="w-5 h-5 text-indigo-400" />
                    User Accounts
                    {userTierFilter !== 'all' && (
                      <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-slate-700 text-slate-300">
                        Showing: {userTierFilter}
                      </span>
                    )}
                  </h3>
                  
                  <div className="flex gap-3 mb-4">
                    <input
                      type="text"
                      value={userSearch}
                      onChange={(e) => setUserSearch(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && searchUsers()}
                      placeholder="Search by email or name..."
                      className="flex-1 bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-indigo-500"
                      data-testid="user-search-input"
                    />
                    <button
                      onClick={searchUsers}
                      disabled={usersLoading}
                      className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg flex items-center gap-2"
                      data-testid="user-search-btn"
                    >
                      {usersLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                      Search
                    </button>
                  </div>
                  
                  {/* Filtered Users List */}
                  <div className="space-y-2 max-h-[500px] overflow-y-auto">
                    {users
                      .filter(u => !excludeTestEmails || !isTestEmail(u.email))
                      .filter(u => userTierFilter === 'all' || u.current_plan === userTierFilter)
                      .map(user => (
                      <div
                        key={user.id}
                        className={`p-4 bg-slate-900/50 rounded-xl border transition-all ${
                          isTestEmail(user.email) 
                            ? 'border-orange-500/30 bg-orange-900/10' 
                            : 'border-slate-700/30 hover:border-slate-600/50'
                        }`}
                        data-testid={`user-row-${user.id}`}
                      >
                        <div className="flex items-center justify-between gap-4">
                          {/* User Info - Clickable */}
                          <div 
                            className="flex-1 cursor-pointer"
                            onClick={() => openUserDetail(user.id)}
                          >
                            <div className="font-medium text-white flex items-center gap-2">
                              <User className="w-4 h-4 text-slate-400" />
                              {user.name || 'No Name'}
                              {/* Plan Badge */}
                              <span className={`px-2 py-0.5 text-xs rounded-full border ${getPlanBadge(user.current_plan)}`}>
                                {user.current_plan === 'founder' ? 'Founder' : 
                                 user.current_plan === 'family' ? 'Family' : 
                                 user.current_plan === 'lifetime' ? 'Lifetime' : 'Free'}
                              </span>
                              {/* Test Email Badge */}
                              {isTestEmail(user.email) && (
                                <span className="px-2 py-0.5 text-xs rounded-full bg-orange-500/20 text-orange-300 border border-orange-500/30">
                                  Test
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-slate-400">{user.email}</div>
                            <div className="text-xs text-slate-500 mt-1">
                              {user.children_count || 0} children • Joined {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'Unknown'}
                              {user.stripe_customer_id && <span className="text-green-400"> • Stripe</span>}
                            </div>
                          </div>
                          
                          {/* Plan Action Buttons */}
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {user.current_plan !== 'family' && (
                              <button
                                onClick={(e) => { e.stopPropagation(); handlePlanChange(user.id, 'family'); }}
                                disabled={planChangeLoading === user.id}
                                className="px-2 py-1 text-xs bg-indigo-600/20 hover:bg-indigo-600/40 text-indigo-300 rounded border border-indigo-500/30 transition-all disabled:opacity-50"
                                title="Upgrade to Family Plan"
                                data-testid={`set-family-${user.id}`}
                              >
                                {planChangeLoading === user.id ? <RefreshCw className="w-3 h-3 animate-spin" /> : 'Family'}
                              </button>
                            )}
                            {user.current_plan !== 'founder' && (
                              <button
                                onClick={(e) => { e.stopPropagation(); handlePlanChange(user.id, 'founder'); }}
                                disabled={planChangeLoading === user.id}
                                className="px-2 py-1 text-xs bg-amber-600/20 hover:bg-amber-600/40 text-amber-300 rounded border border-amber-500/30 transition-all disabled:opacity-50"
                                title="Upgrade to Founder"
                                data-testid={`set-founder-${user.id}`}
                              >
                                {planChangeLoading === user.id ? <RefreshCw className="w-3 h-3 animate-spin" /> : 'Founder'}
                              </button>
                            )}
                            {user.current_plan !== 'free' && (
                              <button
                                onClick={(e) => { e.stopPropagation(); handlePlanChange(user.id, 'free'); }}
                                disabled={planChangeLoading === user.id}
                                className="px-2 py-1 text-xs bg-red-600/20 hover:bg-red-600/40 text-red-300 rounded border border-red-500/30 transition-all disabled:opacity-50"
                                title="Downgrade to Free"
                                data-testid={`set-free-${user.id}`}
                              >
                                {planChangeLoading === user.id ? <RefreshCw className="w-3 h-3 animate-spin" /> : 'Free'}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                    {users
                      .filter(u => !excludeTestEmails || !isTestEmail(u.email))
                      .filter(u => userTierFilter === 'all' || u.current_plan === userTierFilter)
                      .length === 0 && !usersLoading && (
                      <div className="text-center py-8 text-slate-500">
                        <Users className="w-12 h-12 mx-auto mb-2 opacity-30" />
                        <p>
                          {users.length === 0 
                            ? 'Search for users or load all users' 
                            : excludeTestEmails && users.filter(u => !isTestEmail(u.email)).length === 0
                              ? 'All users are test accounts. Turn off the filter to see them.'
                              : `No ${userTierFilter} users found`
                          }
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* User Detail Panel */}
                {selectedUser && userDetail && (
                  <div className="bg-slate-800/30 border border-slate-700/50 rounded-2xl p-6">
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                        <User className="w-5 h-5 text-cyan-400" />
                        User Details
                      </h3>
                      <button
                        onClick={() => { setSelectedUser(null); setUserDetail(null); setResetResult(null); }}
                        className="text-slate-400 hover:text-white"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                    
                    {/* User Info */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                      <div className="space-y-3">
                        <div>
                          <div className="text-xs text-slate-500 uppercase">Email</div>
                          <div className="text-white">{userDetail.user?.email}</div>
                        </div>
                        <div>
                          <div className="text-xs text-slate-500 uppercase">Name</div>
                          <div className="text-white">{userDetail.user?.name || 'Not set'}</div>
                        </div>
                        <div>
                          <div className="text-xs text-slate-500 uppercase">Registered</div>
                          <div className="text-slate-300">{userDetail.user?.created_at ? new Date(userDetail.user.created_at).toLocaleDateString() : 'Unknown'}</div>
                        </div>
                      </div>
                      
                      {/* Actions */}
                      <div className="space-y-3">
                        <div className="text-xs text-slate-500 uppercase mb-2">Support Actions</div>
                        <button
                          onClick={() => setShowResetConfirm('password')}
                          className="w-full px-4 py-2 bg-amber-600/20 hover:bg-amber-600/30 text-amber-300 rounded-lg flex items-center gap-2 text-sm transition-all"
                          data-testid="reset-password-btn"
                        >
                          <Key className="w-4 h-4" />
                          Reset Password
                        </button>
                        
                        {showResetConfirm === 'password' && (
                          <div className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                            <p className="text-amber-300 text-sm mb-2">Reset password for {userDetail.user?.email}?</p>
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleResetPassword(selectedUser)}
                                className="px-3 py-1 bg-amber-600 hover:bg-amber-700 text-white rounded text-sm"
                              >
                                Confirm Reset
                              </button>
                              <button
                                onClick={() => setShowResetConfirm(null)}
                                className="px-3 py-1 bg-slate-700 hover:bg-slate-600 text-white rounded text-sm"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        )}
                        
                        {resetResult?.temporary_password && (
                          <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                            <p className="text-green-300 text-sm mb-1">Temporary Password:</p>
                            <div className="flex items-center gap-2">
                              <code className="text-green-400 font-mono">{resetResult.temporary_password}</code>
                              <button
                                onClick={() => {
                                  navigator.clipboard.writeText(resetResult.temporary_password);
                                  toast.success('Copied to clipboard');
                                }}
                                className="p-1 hover:bg-green-500/20 rounded"
                              >
                                <Copy className="w-4 h-4 text-green-400" />
                              </button>
                            </div>
                            <p className="text-xs text-slate-400 mt-1">{resetResult.note}</p>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Children */}
                    <div className="mb-6">
                      <div className="text-xs text-slate-500 uppercase mb-3">Children</div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {userDetail.children?.map(child => (
                          <div
                            key={child.id}
                            className="p-3 bg-slate-900/50 rounded-lg border border-slate-700/30"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <div 
                                  className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold"
                                  style={{ backgroundColor: child.avatar_color || '#4ADE80' }}
                                >
                                  {child.name?.[0] || '?'}
                                </div>
                                <div>
                                  <div className="font-medium text-white">{child.name}</div>
                                  <div className="text-xs text-slate-400">{child.sparks || 0} sparks</div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => openChildDetail(child.id)}
                                  className="p-1.5 bg-slate-700/50 hover:bg-slate-600/50 rounded text-slate-300 hover:text-white transition-all"
                                  title="View Activity"
                                >
                                  <Eye className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => setShowResetConfirm(`pin-${child.id}`)}
                                  className="p-1.5 bg-slate-700/50 hover:bg-slate-600/50 rounded text-slate-300 hover:text-white transition-all"
                                  title="Reset PIN"
                                >
                                  <Key className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                            
                            {showResetConfirm === `pin-${child.id}` && (
                              <div className="mt-2 p-2 bg-amber-500/10 border border-amber-500/20 rounded">
                                <p className="text-amber-300 text-xs mb-2">Reset PIN for {child.name}?</p>
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => handleResetPin(child.id)}
                                    className="px-2 py-1 bg-amber-600 hover:bg-amber-700 text-white rounded text-xs"
                                  >
                                    Reset PIN
                                  </button>
                                  <button
                                    onClick={() => setShowResetConfirm(null)}
                                    className="px-2 py-1 bg-slate-700 text-white rounded text-xs"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Login History */}
                    <div>
                      <div className="text-xs text-slate-500 uppercase mb-3 flex items-center gap-2">
                        <History className="w-3 h-3" />
                        Recent Activity
                      </div>
                      <div className="space-y-2 max-h-[200px] overflow-y-auto">
                        {userDetail.login_history?.slice(0, 10).map((activity, i) => (
                          <div key={i} className="text-sm flex items-center justify-between p-2 bg-slate-900/30 rounded">
                            <span className="text-slate-300">{activity.activity_type}</span>
                            <span className="text-slate-500 text-xs">
                              {activity.timestamp ? new Date(activity.timestamp).toLocaleString() : ''}
                            </span>
                          </div>
                        ))}
                        {(!userDetail.login_history || userDetail.login_history.length === 0) && (
                          <p className="text-slate-500 text-sm">No recent activity recorded</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Drill-Down Modal */}
      {drillDownModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 border border-slate-700 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-slate-700">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                {drillDownModal === 'activity' && <><Activity className="w-5 h-5 text-green-400" /> Activity Usage Details</>}
                {drillDownModal === 'learning' && <><BookOpen className="w-5 h-5 text-blue-400" /> Learning Analytics</>}
                {drillDownModal === 'creative' && <><Music className="w-5 h-5 text-fuchsia-400" /> Creative Tools Stats</>}
                {drillDownModal === 'reading' && <><Book className="w-5 h-5 text-amber-400" /> Temple Listens Analytics</>}
                {drillDownModal === 'child' && <><User className="w-5 h-5 text-cyan-400" /> Child Activity Detail</>}
              </h3>
              <button onClick={closeDrillDown} className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6">
              {drillDownLoading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="w-8 h-8 text-indigo-400 animate-spin" />
                </div>
              ) : drillDownData ? (
                <>
                  {/* Activity Usage Drill-Down */}
                  {drillDownModal === 'activity' && (
                    <div className="space-y-6">
                      {/* Courtyard Totals */}
                      <div>
                        <h4 className="text-white font-medium mb-3">Courtyard Activity Totals</h4>
                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                          <div className="p-3 bg-slate-900/50 rounded-lg text-center">
                            <div className="text-2xl font-bold text-fuchsia-400">{drillDownData.courtyard_totals?.beat_forge || 0}</div>
                            <div className="text-xs text-slate-400">Beats</div>
                          </div>
                          <div className="p-3 bg-slate-900/50 rounded-lg text-center">
                            <div className="text-2xl font-bold text-pink-400">{drillDownData.courtyard_totals?.song_forge || 0}</div>
                            <div className="text-xs text-slate-400">Songs</div>
                          </div>
                          <div className="p-3 bg-slate-900/50 rounded-lg text-center">
                            <div className="text-2xl font-bold text-purple-400">{drillDownData.courtyard_totals?.game_forge || 0}</div>
                            <div className="text-xs text-slate-400">Games</div>
                          </div>
                          <div className="p-3 bg-slate-900/50 rounded-lg text-center">
                            <div className="text-2xl font-bold text-indigo-400">{drillDownData.courtyard_totals?.constellations || 0}</div>
                            <div className="text-xs text-slate-400">Constellations</div>
                          </div>
                          <div className="p-3 bg-slate-900/50 rounded-lg text-center">
                            <div className="text-2xl font-bold text-cyan-400">{drillDownData.courtyard_totals?.story_contributions || 0}</div>
                            <div className="text-xs text-slate-400">Story Parts</div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Most Active Children */}
                      <div>
                        <h4 className="text-white font-medium mb-3">Most Active Children (This Week)</h4>
                        <div className="space-y-2">
                          {drillDownData.most_active_children?.map((child, i) => (
                            <div 
                              key={child.child_id} 
                              className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg hover:bg-slate-800/50 cursor-pointer"
                              onClick={() => openChildDetail(child.child_id)}
                            >
                              <div className="flex items-center gap-3">
                                <div className="w-6 h-6 rounded-full bg-green-500/20 text-green-400 flex items-center justify-center text-xs font-bold">
                                  {i + 1}
                                </div>
                                <span className="text-white">{child.child_name}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-slate-400">{child.activity_count} activities</span>
                                <ChevronRight className="w-4 h-4 text-slate-500" />
                              </div>
                            </div>
                          ))}
                          {(!drillDownData.most_active_children || drillDownData.most_active_children.length === 0) && (
                            <p className="text-slate-500 text-sm">No activity data yet</p>
                          )}
                        </div>
                      </div>
                      
                      {/* Time by Activity */}
                      <div>
                        <h4 className="text-white font-medium mb-3">Time Spent by Activity Type</h4>
                        <div className="space-y-2">
                          {drillDownData.time_by_activity?.map(activity => (
                            <div key={activity.activity_type} className="flex items-center gap-4 p-2 bg-slate-900/30 rounded">
                              <div className="w-32 text-sm text-slate-300 capitalize">{activity.activity_type?.replace(/_/g, ' ')}</div>
                              <div className="flex-1 h-4 bg-slate-700/50 rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-gradient-to-r from-green-500 to-emerald-500 rounded-full"
                                  style={{ width: `${Math.min((activity.total_minutes / 60) * 100, 100)}%` }}
                                />
                              </div>
                              <div className="text-right">
                                <span className="text-white">{activity.total_minutes} min</span>
                                <span className="text-slate-500 text-xs ml-2">({activity.sessions} sessions)</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Learning Drill-Down */}
                  {drillDownModal === 'learning' && (
                    <div className="space-y-6">
                      {/* Top Lessons */}
                      <div>
                        <h4 className="text-white font-medium mb-3">Most Completed Lessons</h4>
                        <div className="space-y-2">
                          {drillDownData.top_lessons?.map((lesson, i) => (
                            <div key={lesson.lesson_id} className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg">
                              <div className="flex items-center gap-3">
                                <div className="w-6 h-6 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center text-xs font-bold">
                                  {i + 1}
                                </div>
                                <div>
                                  <div className="text-white">{lesson.title}</div>
                                  <div className="text-xs text-slate-400">{lesson.realm} • {lesson.difficulty}</div>
                                </div>
                              </div>
                              <div className="text-green-400 font-medium">{lesson.completions} completions</div>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      {/* Active Journeys */}
                      <div>
                        <h4 className="text-white font-medium mb-3">Active Learning Journeys</h4>
                        <div className="space-y-2">
                          {drillDownData.active_journeys?.slice(0, 10).map(journey => (
                            <div key={journey.journey_id} className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg">
                              <div className="flex items-center gap-2">
                                <MapPin className="w-4 h-4 text-purple-400" />
                                <span className="text-white">{journey.journey_name}</span>
                              </div>
                              <div className="text-sm">
                                <span className="text-slate-300">{journey.active_children} kids</span>
                                <span className="text-slate-500 mx-2">•</span>
                                <span className="text-green-400">{journey.lessons_completed} lessons done</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      {/* Least Used Lessons */}
                      <div>
                        <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4 text-amber-400" />
                          Least Used Lessons (May Need Attention)
                        </h4>
                        <div className="space-y-2">
                          {drillDownData.least_used_lessons?.map(lesson => (
                            <div key={lesson.lesson_id} className="flex items-center justify-between p-2 bg-amber-500/5 rounded-lg border border-amber-500/10">
                              <div>
                                <div className="text-white text-sm">{lesson.title}</div>
                                <div className="text-xs text-slate-500">{lesson.realm}</div>
                              </div>
                              <div className="text-amber-400 text-sm">{lesson.completions} completions</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Creative Tools Drill-Down */}
                  {drillDownModal === 'creative' && (
                    <div className="space-y-6">
                      {/* Totals */}
                      <div className="grid grid-cols-3 gap-4">
                        <div className="p-4 bg-fuchsia-500/10 border border-fuchsia-500/20 rounded-xl text-center">
                          <div className="text-3xl font-bold text-fuchsia-400">{drillDownData.totals?.beats || 0}</div>
                          <div className="text-sm text-slate-400">Total Beats</div>
                        </div>
                        <div className="p-4 bg-pink-500/10 border border-pink-500/20 rounded-xl text-center">
                          <div className="text-3xl font-bold text-pink-400">{drillDownData.totals?.songs || 0}</div>
                          <div className="text-sm text-slate-400">Total Songs</div>
                        </div>
                        <div className="p-4 bg-purple-500/10 border border-purple-500/20 rounded-xl text-center">
                          <div className="text-3xl font-bold text-purple-400">{drillDownData.totals?.games || 0}</div>
                          <div className="text-sm text-slate-400">Total Games</div>
                        </div>
                      </div>
                      
                      {/* Top Creators */}
                      <div>
                        <h4 className="text-white font-medium mb-3">Top Creators</h4>
                        <div className="space-y-2">
                          {drillDownData.top_creators?.map((creator, i) => (
                            <div 
                              key={creator.child_id} 
                              className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg hover:bg-slate-800/50 cursor-pointer"
                              onClick={() => openChildDetail(creator.child_id)}
                            >
                              <div className="flex items-center gap-3">
                                <div className="w-6 h-6 rounded-full bg-fuchsia-500/20 text-fuchsia-400 flex items-center justify-center text-xs font-bold">
                                  {i + 1}
                                </div>
                                <span className="text-white">{creator.child_name}</span>
                              </div>
                              <div className="flex items-center gap-4 text-sm">
                                <span className="text-fuchsia-400">{creator.beats} beats</span>
                                <span className="text-pink-400">{creator.songs} songs</span>
                                <span className="text-purple-400">{creator.games} games</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      {/* Recent Creations */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <h4 className="text-white font-medium mb-3">Recent Beats</h4>
                          <div className="space-y-2 max-h-[300px] overflow-y-auto">
                            {drillDownData.recent_beats?.map(beat => (
                              <div key={beat.id} className="p-2 bg-slate-900/50 rounded text-sm">
                                <div className="text-white">{beat.name}</div>
                                <div className="text-xs text-slate-400">by {beat.child_name} • {beat.tempo} BPM</div>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div>
                          <h4 className="text-white font-medium mb-3">Recent Songs</h4>
                          <div className="space-y-2 max-h-[300px] overflow-y-auto">
                            {drillDownData.recent_songs?.map(song => (
                              <div key={song.id} className="p-2 bg-slate-900/50 rounded text-sm">
                                <div className="text-white">{song.name}</div>
                                <div className="text-xs text-slate-400">by {song.child_name} • {song.duration || '?'}s</div>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div>
                          <h4 className="text-white font-medium mb-3">Recent Games</h4>
                          <div className="space-y-2 max-h-[300px] overflow-y-auto">
                            {drillDownData.recent_games?.map(game => (
                              <div key={game.id} className="p-2 bg-slate-900/50 rounded text-sm">
                                <div className="text-white">{game.name}</div>
                                <div className="text-xs text-slate-400">by {game.child_name} • {game.template}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Reading Drill-Down */}
                  {drillDownModal === 'reading' && (
                    <div className="space-y-6">
                      <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl text-center">
                        <div className="text-3xl font-bold text-amber-400">{drillDownData.total_reading_sessions || 0}</div>
                        <div className="text-sm text-slate-400">Total Reading Sessions</div>
                      </div>
                      
                      {/* Active Readers */}
                      <div>
                        <h4 className="text-white font-medium mb-3">Most Active Readers</h4>
                        <div className="space-y-2">
                          {drillDownData.active_readers?.map((reader, i) => (
                            <div 
                              key={reader.child_id} 
                              className="flex items-center justify-between p-3 bg-slate-900/50 rounded-lg hover:bg-slate-800/50 cursor-pointer"
                              onClick={() => openChildDetail(reader.child_id)}
                            >
                              <div className="flex items-center gap-3">
                                <div className="w-6 h-6 rounded-full bg-amber-500/20 text-amber-400 flex items-center justify-center text-xs font-bold">
                                  {i + 1}
                                </div>
                                <span className="text-white">{reader.child_name}</span>
                              </div>
                              <div className="text-sm">
                                <span className="text-amber-400">{reader.sessions} sessions</span>
                                <span className="text-slate-500 mx-2">•</span>
                                <span className="text-slate-300">{reader.total_minutes} min</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      {/* Recent Reading */}
                      <div>
                        <h4 className="text-white font-medium mb-3">Recent Reading Activity</h4>
                        <div className="space-y-2 max-h-[300px] overflow-y-auto">
                          {drillDownData.recent_reading?.map((activity, i) => (
                            <div key={i} className="flex items-center justify-between p-2 bg-slate-900/50 rounded">
                              <div>
                                <div className="text-white text-sm">{activity.child_name}</div>
                                <div className="text-xs text-slate-400">{activity.book_title || 'Unknown book'}</div>
                              </div>
                              <div className="text-right">
                                <div className="text-xs text-slate-300">{activity.activity_type}</div>
                                {activity.timestamp && (
                                  <div className="text-xs text-slate-500">{new Date(activity.timestamp).toLocaleDateString()}</div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Child Detail View */}
                  {drillDownModal === 'child' && drillDownData && (
                    <div className="space-y-6">
                      {/* Child Header */}
                      <div className="flex items-center gap-4 p-4 bg-slate-900/50 rounded-xl">
                        <div 
                          className="w-16 h-16 rounded-full flex items-center justify-center text-white text-2xl font-bold"
                          style={{ backgroundColor: drillDownData.child?.avatar_color || '#4ADE80' }}
                        >
                          {drillDownData.child?.name?.[0] || '?'}
                        </div>
                        <div>
                          <div className="text-xl font-bold text-white">{drillDownData.child?.name}</div>
                          <div className="text-slate-400">
                            Parent: {drillDownData.parent?.email || 'Unknown'}
                          </div>
                          <div className="text-sm text-yellow-400 flex items-center gap-1 mt-1">
                            <Sparkles className="w-4 h-4" />
                            {drillDownData.child?.sparks || 0} sparks
                          </div>
                        </div>
                      </div>
                      
                      {/* Stats Grid */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <div className="p-3 bg-slate-900/50 rounded-lg text-center">
                          <div className="text-2xl font-bold text-green-400">{drillDownData.stats?.total_lessons_completed || 0}</div>
                          <div className="text-xs text-slate-400">Lessons Done</div>
                        </div>
                        <div className="p-3 bg-slate-900/50 rounded-lg text-center">
                          <div className="text-2xl font-bold text-fuchsia-400">{drillDownData.stats?.beats_created || 0}</div>
                          <div className="text-xs text-slate-400">Beats</div>
                        </div>
                        <div className="p-3 bg-slate-900/50 rounded-lg text-center">
                          <div className="text-2xl font-bold text-pink-400">{drillDownData.stats?.songs_created || 0}</div>
                          <div className="text-xs text-slate-400">Songs</div>
                        </div>
                        <div className="p-3 bg-slate-900/50 rounded-lg text-center">
                          <div className="text-2xl font-bold text-purple-400">{drillDownData.stats?.games_created || 0}</div>
                          <div className="text-xs text-slate-400">Games</div>
                        </div>
                      </div>
                      
                      {/* Recent Activity */}
                      <div>
                        <h4 className="text-white font-medium mb-3">Recent Activity</h4>
                        <div className="space-y-2 max-h-[200px] overflow-y-auto">
                          {drillDownData.recent_activities?.slice(0, 15).map((activity, i) => (
                            <div key={i} className="flex items-center justify-between p-2 bg-slate-900/30 rounded text-sm">
                              <span className="text-slate-300 capitalize">{activity.activity_type?.replace(/_/g, ' ')}</span>
                              <span className="text-slate-500 text-xs">
                                {activity.timestamp ? new Date(activity.timestamp).toLocaleString() : ''}
                              </span>
                            </div>
                          ))}
                          {(!drillDownData.recent_activities || drillDownData.recent_activities.length === 0) && (
                            <p className="text-slate-500 text-sm">No recent activity</p>
                          )}
                        </div>
                      </div>
                      
                      {/* Creative Works */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <h4 className="text-white font-medium mb-2">Beats Created</h4>
                          <div className="space-y-1 max-h-[150px] overflow-y-auto">
                            {drillDownData.creative?.beats?.map(beat => (
                              <div key={beat.id} className="p-2 bg-fuchsia-500/10 rounded text-sm">
                                <span className="text-white">{beat.name}</span>
                                <span className="text-slate-400 ml-2">{beat.tempo} BPM</span>
                              </div>
                            ))}
                            {(!drillDownData.creative?.beats || drillDownData.creative.beats.length === 0) && (
                              <p className="text-slate-500 text-xs">No beats yet</p>
                            )}
                          </div>
                        </div>
                        <div>
                          <h4 className="text-white font-medium mb-2">Songs Created</h4>
                          <div className="space-y-1 max-h-[150px] overflow-y-auto">
                            {drillDownData.creative?.songs?.map(song => (
                              <div key={song.id} className="p-2 bg-pink-500/10 rounded text-sm">
                                <span className="text-white">{song.name}</span>
                              </div>
                            ))}
                            {(!drillDownData.creative?.songs || drillDownData.creative.songs.length === 0) && (
                              <p className="text-slate-500 text-xs">No songs yet</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-12 text-slate-500">No data available</div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* New Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-cyan-400" />
                Create Beta Invite
              </h3>
              <button
                onClick={() => setShowInviteModal(false)}
                className="text-slate-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-slate-400 mb-1">Parent Name</label>
                <input
                  type="text"
                  value={newInvite.parent_name}
                  onChange={(e) => setNewInvite({ ...newInvite, parent_name: e.target.value })}
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  placeholder="John & Jane Smith"
                  data-testid="invite-parent-name"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1">Parent Email</label>
                <input
                  type="email"
                  value={newInvite.parent_email}
                  onChange={(e) => setNewInvite({ ...newInvite, parent_email: e.target.value })}
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  placeholder="family@example.com"
                  data-testid="invite-parent-email"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1">Notes (Optional)</label>
                <textarea
                  value={newInvite.notes}
                  onChange={(e) => setNewInvite({ ...newInvite, notes: e.target.value })}
                  className="w-full bg-slate-900 border border-slate-700 rounded-lg px-4 py-2 text-white focus:ring-2 focus:ring-cyan-500 focus:border-transparent resize-none"
                  placeholder="e.g., 2 kids ages 6 and 8, friend referral"
                  rows={2}
                  data-testid="invite-notes"
                />
              </div>
              <div className="text-xs text-slate-500">
                Invite expires in 14 days. A unique code (e.g., TEMPLE-XXX) will be generated.
              </div>
            </div>
            
            <div className="flex items-center gap-3 mt-6">
              <button
                onClick={() => setShowInviteModal(false)}
                className="flex-1 px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-all"
              >
                Cancel
              </button>
              <button
                onClick={createBetaInvite}
                disabled={inviteLoading}
                className="flex-1 px-4 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="submit-invite-btn"
              >
                {inviteLoading ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Link2 className="w-4 h-4" />
                    Generate Invite
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Email Template Modal */}
      {showEmailModal && selectedInviteForEmail && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Mail className="w-5 h-5 text-cyan-400" />
                Send Beta Invite
              </h3>
              <button
                onClick={() => { setShowEmailModal(false); setSelectedInviteForEmail(null); }}
                className="text-slate-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Recipient Info */}
            <div className="bg-slate-900/50 rounded-lg p-4 mb-4">
              <div className="text-sm text-slate-400 mb-1">To:</div>
              <div className="text-white font-medium">{selectedInviteForEmail.parent_name}</div>
              <div className="text-cyan-400">{selectedInviteForEmail.parent_email}</div>
              <div className="mt-2 text-sm text-slate-400">Invite Code: <code className="text-amber-400">{selectedInviteForEmail.invite_code}</code></div>
            </div>
            
            {/* Email Subject */}
            <div className="mb-3">
              <div className="text-sm text-slate-400 mb-1">Subject:</div>
              <div className="bg-slate-900/50 rounded-lg px-3 py-2 text-white font-medium">
                Invitation: Temple of Children Family Beta
              </div>
            </div>
            
            {/* Email Body */}
            <div className="flex-1 overflow-y-auto mb-4">
              <div className="text-sm text-slate-400 mb-1">Message:</div>
              <pre className="bg-slate-900/50 rounded-lg p-4 text-slate-300 text-sm whitespace-pre-wrap font-sans overflow-x-hidden">
                {generateEmailTemplate(selectedInviteForEmail)}
              </pre>
            </div>
            
            {/* Action Buttons */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => { setShowEmailModal(false); setSelectedInviteForEmail(null); }}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-all"
              >
                Cancel
              </button>
              <button
                onClick={copyEmailTemplate}
                className="flex-1 px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-all flex items-center justify-center gap-2"
              >
                <Copy className="w-4 h-4" />
                Copy Email
              </button>
              <button
                onClick={sendAndMarkSent}
                className="flex-1 px-4 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg transition-all flex items-center justify-center gap-2"
                data-testid="confirm-send-btn"
              >
                <Check className="w-4 h-4" />
                Mark as Sent
              </button>
            </div>
            
            <div className="mt-3 text-xs text-slate-500 text-center">
              Copy the email content above and send it manually to the parent. Click "Mark as Sent" after sending.
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalyticsDashboardPage;
